import { For, Show, createResource, createSignal, type Component } from 'solid-js';

import {
  ApiError,
  createCharacter,
  deleteCharacter,
  logout,
  roster,
  type Account,
  type RosterCharacter,
} from '../net/account';
import {
  archetypeBlurb,
  archetypeName,
  armourSetBlurb,
  armourSetName,
  canCreate,
  inPlayOrder,
  nameProblem,
  playedAgo,
  slotsLeft,
} from './roster';

/**
 * The character selector and creator: what stands between signing in and playing.
 *
 * ── Everything it offers comes from the server ──────────────────────────────
 *
 * The colours, the starting kits and the leagues arrive with the roster. **Not a table
 * here**, because they are validated server-side on the way back in — a client list that
 * had drifted would offer a choice that is refused on submit, and the player would have no
 * way to tell which of their four choices was the bad one.
 *
 * ── Why deletion asks twice ─────────────────────────────────────────────────
 *
 * A character is the only thing in this game that cannot be got back, and the button sits
 * on a row the player is aiming at to *play*. The confirmation is a second click on the
 * same row rather than a modal: a modal over a list is a thing to dismiss, and this has to
 * be harder to do by accident than to do on purpose.
 */
export const CharacterSelect: Component<{
  account: Account;
  /** Enters the world as this character. The caller opens the socket. */
  onPlay: (character: RosterCharacter) => void;
  /** Signed out — the caller goes back to the login screen. */
  onSignedOut: () => void;
}> = (props) => {
  // The fetcher catches rather than rejecting, and the failure becomes a signal.
  //
  // `createResource`'s own `error` is a rejected promise the framework re-throws at the
  // nearest `<ErrorBoundary>`, and there is none above the gate — so a roster that would not
  // load rendered *nothing* instead of the retry below. An explicit signal keeps the failure
  // where it can be drawn, which for this screen is the whole point: there is nothing else
  // on it to act on.
  const [loadError, setLoadError] = createSignal<string | null>(null);
  const [data, { mutate, refetch }] = createResource(async () => {
    setLoadError(null);
    try {
      return await roster();
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Could not read your roster.');
      return undefined;
    }
  });
  const [error, setError] = createSignal<string | null>(null);
  const [creating, setCreating] = createSignal(false);
  const [confirming, setConfirming] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);

  const characters = () => inPlayOrder(data()?.characters ?? []);

  // The draft. Seeded from the server's own lists the first time they arrive, so the
  // creator opens with a valid selection rather than three empty choices.
  const [draft, setDraft] = createSignal({
    name: '',
    league: '',
    colour: '',
    archetype: '',
    armourSet: '',
  });
  const patch = (over: Partial<ReturnType<typeof draft>>) => setDraft({ ...draft(), ...over });

  const openCreator = () => {
    const d = data();
    if (!d) return;
    setDraft({
      name: '',
      league: d.leagues.find((l) => l.main)?.id ?? d.leagues[0]?.id ?? '',
      colour: d.colours[0] ?? '',
      archetype: d.archetypes[0] ?? '',
      // Leather, when the server offers it: it is the middle of the three and what every
      // character wore before the choice existed, so the default is not a new opinion.
      armourSet: d.armourSets.find((s) => s === 'LEATHER') ?? d.armourSets[0] ?? '',
    });
    setError(null);
    setCreating(true);
  };

  const submit = async (event: Event) => {
    event.preventDefault();
    const d = data();
    if (!d || busy() || !canCreate(draft(), d)) return;

    setBusy(true);
    setError(null);
    try {
      const created = await createCharacter(draft());
      // Spliced in rather than refetched: the server has already told us exactly what it
      // made, and a round trip here would blank the list for a frame.
      mutate({ ...d, characters: [created, ...d.characters] });
      setCreating(false);
    } catch (err) {
      // The server's sentence — *that name is taken in this league* is the whole of what
      // the player needs, and it is not something this client could have known.
      setError(err instanceof ApiError ? err.message : 'The character could not be created.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    setBusy(true);
    setError(null);
    try {
      await deleteCharacter(id);
      const d = data();
      if (d) mutate({ ...d, characters: d.characters.filter((c) => c.id !== id) });
      setConfirming(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That character could not be deleted.');
    } finally {
      setBusy(false);
    }
  };

  const signOut = async () => {
    try {
      await logout();
      props.onSignedOut();
    } catch {
      // **A failed logout must not look like a successful one.** This client cannot clear
      // the cookie itself — that is the `HttpOnly` property working as intended — so
      // showing a login screen over a session that is still live would be a lie, and on a
      // shared machine it is the lie that matters.
      setError('Could not sign out — you are still signed in. Try again.');
    }
  };

  return (
    <div class="gate" data-testid="character-select">
      <div class="plate plate--riveted gate__panel gate__panel--wide">
        <header class="gate__head">
          <h1 class="gate__title">Characters</h1>
          <p class="gate__lede" data-testid="select-account">
            Signed in as {props.account.username}
          </p>
          <button
            type="button"
            class="skills__button"
            data-testid="sign-out"
            onClick={() => void signOut()}
          >
            Sign out
          </button>
        </header>

        <Show when={error()}>
          {(message) => (
            <p class="gate__error" data-testid="select-error" role="alert">
              {message()}
            </p>
          )}
        </Show>

        <Show when={data.loading}>
          <p class="gate__note" data-testid="select-loading">
            Reading your roster…
          </p>
        </Show>

        {/* A failed *load* is different from a failed action: there is nothing on screen to
            act on, so it offers a retry rather than an apology. */}
        <Show when={loadError()}>
          <p class="gate__error" data-testid="select-load-error" role="alert">
            Could not read your roster.{' '}
            <button type="button" class="skills__button" onClick={() => void refetch()}>
              Try again
            </button>
          </p>
        </Show>

        <Show when={data()}>
          {(d) => (
            <>
              <Show when={!creating()}>
                <Show
                  when={characters().length > 0}
                  fallback={
                    <p class="gate__note" data-testid="select-empty">
                      No characters yet. Make one to begin.
                    </p>
                  }
                >
                  <ul class="roster" data-testid="roster">
                    <For each={characters()}>
                      {(character) => (
                        <li class="roster__row" data-testid={`roster-${character.id}`}>
                          <button
                            type="button"
                            class="roster__play"
                            data-testid={`play-${character.id}`}
                            onClick={() => props.onPlay(character)}
                          >
                            <span
                              class="roster__swatch"
                              style={{ background: character.colour }}
                              aria-hidden="true"
                            />
                            <span class="roster__name">{character.name}</span>
                            <span class="roster__meta">
                              <Show when={character.level !== undefined} fallback={<>—</>}>
                                Level {character.level}
                              </Show>
                              {' · '}
                              {archetypeName(character.archetype)}
                            </span>
                            <span class="roster__played">{playedAgo(character.playedAt)}</span>
                          </button>

                          {/* Two clicks, on the same row, rather than a modal. A character
                              is the only thing here that cannot be got back, and the
                              button sits where the player is aiming to *play*. */}
                          <Show
                            when={confirming() === character.id}
                            fallback={
                              <button
                                type="button"
                                class="roster__delete"
                                data-testid={`delete-${character.id}`}
                                aria-label={`Delete ${character.name}`}
                                onClick={() => setConfirming(character.id)}
                              >
                                ✕
                              </button>
                            }
                          >
                            <span class="roster__confirm">
                              <button
                                type="button"
                                class="roster__delete roster__delete--armed"
                                data-testid={`delete-confirm-${character.id}`}
                                disabled={busy()}
                                onClick={() => void remove(character.id)}
                              >
                                Delete for good
                              </button>
                              <button
                                type="button"
                                class="skills__button"
                                data-testid={`delete-cancel-${character.id}`}
                                onClick={() => setConfirming(null)}
                              >
                                Keep
                              </button>
                            </span>
                          </Show>
                        </li>
                      )}
                    </For>
                  </ul>
                </Show>

                <p class="gate__note" data-testid="slots-left">
                  {slotsLeft(d())} of {d().max} character slots free
                </p>

                <button
                  type="button"
                  class="sigil-button gate__submit"
                  data-testid="new-character"
                  disabled={slotsLeft(d()) === 0}
                  onClick={openCreator}
                >
                  <span>{slotsLeft(d()) === 0 ? 'All slots used' : 'New Character'}</span>
                </button>
              </Show>

              {/* ── The creator ─────────────────────────────────────────── */}
              <Show when={creating()}>
                <form class="creator" data-testid="creator" onSubmit={submit}>
                  <label class="gate__field">
                    <span>Name</span>
                    <input
                      type="text"
                      data-testid="creator-name"
                      value={draft().name}
                      ref={(el) => queueMicrotask(() => el.focus())}
                      onInput={(e) => patch({ name: e.currentTarget.value })}
                    />
                  </label>
                  {/* Said while typing rather than after pressing create. The server still
                      decides — it knows the two things this cannot, whether the name is
                      taken and whether the roster is full. */}
                  <Show when={draft().name !== '' && nameProblem(draft().name)}>
                    {(problem) => (
                      <p class="gate__hint" data-testid="creator-name-problem">
                        {problem()}
                      </p>
                    )}
                  </Show>

                  <fieldset class="creator__group">
                    <legend>Starting kit</legend>
                    <div class="creator__kits">
                      <For each={d().archetypes}>
                        {(id) => (
                          <button
                            type="button"
                            class="creator__kit"
                            classList={{ 'creator__kit--on': draft().archetype === id }}
                            data-testid={`kit-${id}`}
                            aria-pressed={draft().archetype === id}
                            onClick={() => patch({ archetype: id })}
                          >
                            <strong>{archetypeName(id)}</strong>
                            <span>{archetypeBlurb(id)}</span>
                          </button>
                        )}
                      </For>
                    </div>
                  </fieldset>

                  <fieldset class="creator__group">
                    <legend>Armour</legend>
                    <div class="creator__kits">
                      <For each={d().armourSets}>
                        {(id) => (
                          <button
                            type="button"
                            class="creator__kit"
                            classList={{ 'creator__kit--on': draft().armourSet === id }}
                            data-testid={`armour-${id}`}
                            aria-pressed={draft().armourSet === id}
                            onClick={() => patch({ armourSet: id })}
                          >
                            <strong>{armourSetName(id)}</strong>
                            <span>{armourSetBlurb(id)}</span>
                          </button>
                        )}
                      </For>
                    </div>
                  </fieldset>

                  <fieldset class="creator__group">
                    <legend>Colour</legend>
                    <div class="creator__colours">
                      <For each={d().colours}>
                        {(colour) => (
                          <button
                            type="button"
                            class="creator__colour"
                            classList={{ 'creator__colour--on': draft().colour === colour }}
                            style={{ background: colour }}
                            data-testid={`colour-${colour}`}
                            aria-label={colour}
                            aria-pressed={draft().colour === colour}
                            onClick={() => patch({ colour })}
                          />
                        )}
                      </For>
                    </div>
                  </fieldset>

                  <fieldset class="creator__group">
                    <legend>League</legend>
                    <div class="creator__leagues">
                      <For each={d().leagues}>
                        {(league) => (
                          <button
                            type="button"
                            class="creator__league"
                            classList={{ 'creator__league--on': draft().league === league.id }}
                            data-testid={`league-${league.id}`}
                            aria-pressed={draft().league === league.id}
                            onClick={() => patch({ league: league.id })}
                          >
                            <strong>{league.name}</strong>
                            <Show when={league.main}>
                              <span class="creator__main">Main</span>
                            </Show>
                          </button>
                        )}
                      </For>
                    </div>
                  </fieldset>

                  <div class="creator__actions">
                    <button
                      type="submit"
                      class="sigil-button"
                      data-testid="creator-submit"
                      disabled={busy() || !canCreate(draft(), d())}
                    >
                      <span>{busy() ? 'Creating…' : 'Create'}</span>
                    </button>
                    <button
                      type="button"
                      class="skills__button"
                      data-testid="creator-cancel"
                      onClick={() => setCreating(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Show>
            </>
          )}
        </Show>
      </div>
    </div>
  );
};
